/**
 * The round photo pin on a canvas: the point's picture in a ring of the pin
 * colour over a short tip, the shape of `nadelbild(…, { bild })` on the map.
 *
 * One place for two pictures (1.127.0): the film (`drawPins()` in
 * `routeFilm.ts`, since 1.99.0) and the still picture of the day card
 * (`zeichneTagesroute()`). From use: "wenn nur ein Bild ausgegeben wird,
 * sollten die POIs ihre Bilder auch anzeigen" -- the film had the photos,
 * the picture only the drops, because the drawing stood in the film alone.
 *
 * Measured from the film's numbers, which stay as they were at `ring` 27:
 * everything scales with the ring, so the still picture takes the map's
 * size (`PHOTO_PIN_MAP`, 40 round as `.nadel--foto`) and the same shape.
 * `x, y` is the tip, the point itself. The centre of the ring is returned,
 * for the film's labels.
 */
export function drawPhotoPin(
  pen: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  photo: CanvasImageSource & { naturalWidth: number; naturalHeight: number },
  colour: string,
  ring: number,
): { cx: number; cy: number } {
  const s = ring / 27
  const cx = x
  const cy = y - 40 * s
  pen.save()
  pen.shadowColor = 'rgba(0,0,0,0.35)'
  pen.shadowBlur = 6 * s
  pen.beginPath()
  pen.moveTo(x - 9 * s, y - 18 * s)
  pen.lineTo(x, y)
  pen.lineTo(x + 9 * s, y - 18 * s)
  pen.closePath()
  pen.fillStyle = colour
  pen.fill()
  pen.beginPath()
  pen.arc(cx, cy, ring, 0, Math.PI * 2)
  pen.fill()
  pen.restore()
  pen.save()
  const inner = ring - 4 * s
  pen.beginPath()
  pen.arc(cx, cy, inner, 0, Math.PI * 2)
  pen.clip()
  // Filling the circle, cut like `object-fit: cover`.
  const scale = Math.max((2 * inner) / photo.naturalWidth, (2 * inner) / photo.naturalHeight)
  const w = photo.naturalWidth * scale
  const h = photo.naturalHeight * scale
  pen.drawImage(photo, cx - w / 2, cy - h / 2, w, h)
  pen.restore()
  return { cx, cy }
}

/** The film's ring (since 1.99.0). */
export const PHOTO_PIN_FILM = 27
/** The map's ring: 40 round, as `.nadel--foto` and `FOTO_BREIT` in `Karte.tsx`. */
export const PHOTO_PIN_MAP = 20
/** How far a pin of this ring reaches above its point: tip and ring together. */
export function photoPinReach(ring: number): number {
  return 40 * (ring / 27) + ring
}
